/**
 * Real fleet data for Fleet Dashboard.
 * - Fast path: get_fleet_dashboard_snapshot(p_org_id) + property_realtime_status + property_resilience_status.
 *   Curtailment/underperformance come from property_realtime_status (persisted by evaluate-automations);
 *   resilience from property_resilience_status. No get_fleet_dashboard_extras RPC.
 * - Consistency: docs/contracts/FLEET_PROPERTY_CONSISTENCY.md — same sources and rules as
 *   property dashboard (poll-status 15/30 min, telemetry, overnight_forecasts, curtailment, resilience).
 * - Refreshes when stale (90s) and every 2 min while on the fleet page.
 *
 * Severity and "why" remain computed by addSeverityToSystems on the client (fleet-severity).
 */

import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { getBatteryStateForDisplay } from '@/lib/energy-state';
import type {
  FleetSystem,
  BatteryState,
  ConfidenceLevel,
  ProviderType,
} from '@/data/fleet-types';

/** Row shape from property_realtime_status table (curtailment + underperformance). */
interface PropertyRealtimeStatusRow {
  property_id: string;
  is_curtailing: boolean;
  wasted_kwh_today: number | null;
  curtailment_confidence: string | null;
  is_underperforming: boolean;
  underperformance_pct: number | null;
  pv_underperformance_score: number | null;
  pv_state?: string | null;
}

/** Row shape from property_resilience_status table. */
interface PropertyResilienceStatusRow {
  property_id: string;
  risk_active: boolean;
  severity: string | null;
}

/** Row shape from get_fleet_dashboard_snapshot RPC (snake_case). */
interface FleetDashboardSnapshotRow {
  property_id: string;
  pv_state?: string | null;
  system_name: string | null;
  city: string | null;
  state: string | null;
  provider: string | null;
  online: boolean;
  data_stale: boolean;
  last_poll_at: string | null;
  last_poll_status: string | null;
  last_poll_error: string | null;
  pv_kw: number | null;
  load_kw: number | null;
  batt_kw: number | null;
  grid_kw: number | null;
  gen_kw: number | null;
  soc_pct: number | null;
  battery_capacity_kwh: number | null;
  available_kwh: number | null;
  reserve_kwh: number | null;
  has_overnight_forecast: boolean;
  overnight_kwh_at_sunrise: number | null;
  forecast_risk: boolean;
  grid_mode: string | null;
  breach_time: string | null;
  breach_probability: number | null;
  projected_min_soc_pct: number | null;
  sunrise_soc_pct: number | null;
  forecast_confidence: string | null;
  is_curtailing: boolean;
  wasted_kwh_today: number | null;
  curtailment_score: number | null;
  is_underperforming: boolean;
  underperformance_pct: number | null;
  pv_underperformance_score: number | null;
  resilience_risk: boolean;
  resilience_severity: string | null;
  last_telemetry_at: string | null;
  time_to_depletion_hours: number | null;
  has_telemetry: boolean;
  grid_import_kwh_today: number | null;
  grid_export_kwh_today: number | null;
}

function mapRowToFleetSystem(row: FleetDashboardSnapshotRow): FleetSystem {
  const hasTelemetry = row.has_telemetry ?? false;
  const pvKw = Number(row.pv_kw ?? 0);
  const loadKw = Number(row.load_kw ?? 0);
  const battKw = Number(row.batt_kw ?? 0);
  const capacityKwh = Number(row.battery_capacity_kwh ?? 30);
  const socPct = Number(row.soc_pct ?? 0);
  const availableKwh = row.available_kwh != null ? Number(row.available_kwh) : (socPct / 100) * capacityKwh;
  const confidence = (row.forecast_confidence === 'high' || row.forecast_confidence === 'medium' || row.forecast_confidence === 'low'
    ? row.forecast_confidence
    : 'low') as ConfidenceLevel;
  const forecastConfidence: 'low' | 'medium' | 'high' =
    confidence === 'high' || confidence === 'medium' || confidence === 'low' ? confidence : 'low';
  const batteryState: BatteryState = getBatteryStateForDisplay(battKw, pvKw, loadKw);
  const knownProviders: ProviderType[] = ['solark', 'eg4', 'franklinwh', 'solaredge', 'enphase', 'tesla'];
  const provider: ProviderType = knownProviders.includes(row.provider as ProviderType)
    ? (row.provider as ProviderType)
    : 'unknown';

  return {
    id: row.property_id,
    systemName: row.system_name ?? 'Unnamed',
    location: {
      city: row.city ?? '',
      state: row.state ?? '',
    },
    provider,
    online: row.online,
    dataStale: row.data_stale,
    lastPollAt: row.last_poll_at ?? null,
    lastPollStatus: row.last_poll_status ?? null,
    lastPollError: row.last_poll_error ?? null,
    hasOvernightForecast: row.has_overnight_forecast,
    forecastRisk: row.forecast_risk,
    // When property.grid_mode is unset (null), infer from telemetry: grid_kw non-null means we have grid meter data → treat as grid_primary for display so grid-tied systems aren't all labeled off-grid.
    gridMode: (row.grid_mode === 'off_grid' || row.grid_mode === 'grid_primary'
      ? row.grid_mode
      : row.grid_kw != null
        ? ('grid_primary' as const)
        : null) as 'off_grid' | 'grid_primary' | null,
    pvKw,
    battKw,
    loadKw,
    gridKw: row.grid_kw != null ? Number(row.grid_kw) : null,
    genKw: row.gen_kw != null ? Number(row.gen_kw) : null,
    batteryState,
    socPct,
    batteryCapacityKwh: capacityKwh,
    availableKwh,
    overnightKwhAtSunrise: Number(row.overnight_kwh_at_sunrise ?? 0),
    confidence,
    resilienceRisk: row.resilience_risk,
    resilienceSeverity: (row.resilience_severity === 'warning' || row.resilience_severity === 'critical' ? row.resilience_severity : undefined),
    reserve_kwh: Number(row.reserve_kwh ?? 0),
    projected_min_soc_pct: Number(row.projected_min_soc_pct ?? 0),
    projected_breach_time: row.breach_time ?? null,
    breach_probability: Number(row.breach_probability ?? 0),
    pv_underperformance_score: Number(row.pv_underperformance_score ?? 0),
    curtailment_score: Number(row.curtailment_score ?? 0),
    forecast_confidence: forecastConfidence,
    why: { primary: 'System operating within expected range.' },
    current_soc_kwh: availableKwh,
    is_curtailing: row.is_curtailing,
    pvState: row.pv_state ?? null,
    wasted_kwh_today: Number(row.wasted_kwh_today ?? 0),
    is_underperforming: row.is_underperforming,
    underperformance_pct: row.underperformance_pct != null ? Number(row.underperformance_pct) : undefined,
    lastTelemetryAt: row.last_telemetry_at ?? null,
    timeToDepletionHours: row.time_to_depletion_hours != null ? Number(row.time_to_depletion_hours) : null,
    hasTelemetry,
    gridImportKwhToday: row.grid_import_kwh_today != null ? Number(row.grid_import_kwh_today) : 0,
    gridExportKwhToday: row.grid_export_kwh_today != null ? Number(row.grid_export_kwh_today) : 0,
  };
}

export interface FleetSystemsResult {
  systems: FleetSystem[];
  fleetUptimeOnlinePct: number;
  fleetUptimeOfflinePct: number;
  overnightRiskAtRiskPct: number;
  overnightRiskSafePct: number;
  overnightRiskGridTiedPct: number;
  overnightRiskNoForecastPct: number;
}

export function useFleetSystems(): {
  data: FleetSystemsResult | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  isFetching: boolean;
} {
  const { userProfile, properties, loading: appLoading, overrideOrgId } = useApp();

  const orgId = overrideOrgId ?? userProfile?.org_id ?? null;

  const propertyIdsKey = properties?.length ? properties.map((p) => p.id).sort().join(',') : '';

  const query = useQuery({
    queryKey: ['fleet-systems', orgId, propertyIdsKey],
    queryFn: async (): Promise<FleetSystemsResult> => {
      if (!orgId || !properties?.length) {
        return {
          systems: [],
          fleetUptimeOnlinePct: 0,
          fleetUptimeOfflinePct: 0,
          overnightRiskAtRiskPct: 0,
          overnightRiskSafePct: 0,
          overnightRiskGridTiedPct: 0,
          overnightRiskNoForecastPct: 0,
        };
      }

      // Populate fleet cache from per-property rows (avoids one heavy org query).
      await Promise.allSettled(
        properties.map((p) =>
          supabase.rpc('refresh_fleet_cache_for_property', { p_property_id: p.id })
        )
      );

      const propertyIds = properties.map((p) => p.id);
      const propertyIdsSet = new Set(propertyIds);

      const [snapshotResult, realtimeResult, resilienceResult] = await Promise.all([
        supabase.rpc('get_fleet_dashboard_snapshot', { p_org_id: orgId }),
        propertyIds.length
          ? supabase.from('property_realtime_status').select('property_id, is_curtailing, wasted_kwh_today, curtailment_confidence, is_underperforming, underperformance_pct, pv_underperformance_score, pv_state').in('property_id', propertyIds)
          : Promise.resolve({ data: [] as PropertyRealtimeStatusRow[] }),
        propertyIds.length
          ? supabase.from('property_resilience_status').select('property_id, risk_active, severity').in('property_id', propertyIds)
          : Promise.resolve({ data: [] as PropertyResilienceStatusRow[] }),
      ]);

      const { data: rows, error: rpcError } = snapshotResult;
      if (rpcError) {
        const detail = rpcError.details ? ` (${JSON.stringify(rpcError.details)})` : '';
        throw new Error(`${rpcError.message}${detail}`);
      }

      let snapshotRows = (rows ?? []) as FleetDashboardSnapshotRow[];
      snapshotRows = snapshotRows.filter((r) => propertyIdsSet.has(r.property_id));

      const realtimeByProperty = new Map<string, PropertyRealtimeStatusRow>();
      for (const r of (realtimeResult.data ?? []) as PropertyRealtimeStatusRow[]) {
        realtimeByProperty.set(r.property_id, r);
      }
      const resilienceByProperty = new Map<string, PropertyResilienceStatusRow>();
      for (const r of (resilienceResult.data ?? []) as PropertyResilienceStatusRow[]) {
        resilienceByProperty.set(r.property_id, r);
      }

      const mergedRows: FleetDashboardSnapshotRow[] = snapshotRows.map((row) => {
        const realtime = realtimeByProperty.get(row.property_id);
        const resilience = resilienceByProperty.get(row.property_id);
        return {
          ...row,
          is_curtailing: realtime?.is_curtailing ?? row.is_curtailing ?? false,
          wasted_kwh_today: realtime?.wasted_kwh_today ?? row.wasted_kwh_today ?? null,
          is_underperforming: realtime?.is_underperforming ?? row.is_underperforming ?? false,
          underperformance_pct: realtime?.underperformance_pct ?? row.underperformance_pct ?? null,
          pv_underperformance_score: realtime?.pv_underperformance_score ?? row.pv_underperformance_score ?? null,
          pv_state: realtime?.pv_state ?? null,
          resilience_risk: resilience?.risk_active ?? row.resilience_risk ?? false,
          resilience_severity: resilience?.severity ?? row.resilience_severity ?? null,
        };
      });
      const systems = mergedRows.map(mapRowToFleetSystem);

      const total = systems.length;
      const onlineCount = systems.filter((s) => s.online).length;
      const offlineCount = total - onlineCount;
      const onlineSystems = systems.filter((s) => s.online);
      const gridPrimaryCount = onlineSystems.filter((s) => s.gridMode === 'grid_primary').length;
      const overnightEligible = onlineSystems.filter(
        (s) => s.gridMode === 'off_grid' || s.gridMode == null
      );
      const atRiskCount = overnightEligible.filter((s) => s.forecastRisk).length;
      const safeCount = overnightEligible.filter((s) => s.hasOvernightForecast && !s.forecastRisk).length;
      const noForecastCount = overnightEligible.filter((s) => !s.hasOvernightForecast).length;
      const fleetUptimeOnlinePct = total ? Math.round((onlineCount / total) * 100) : 0;
      const fleetUptimeOfflinePct = total ? Math.round((offlineCount / total) * 100) : 0;
      const overnightDenom = onlineCount || 1;
      const overnightRiskAtRiskPct = Math.round((atRiskCount / overnightDenom) * 100);
      const overnightRiskSafePct = Math.round((safeCount / overnightDenom) * 100);
      const overnightRiskGridTiedPct = Math.round((gridPrimaryCount / overnightDenom) * 100);
      const overnightRiskNoForecastPct = Math.round((noForecastCount / overnightDenom) * 100);

      return {
        systems,
        fleetUptimeOnlinePct,
        fleetUptimeOfflinePct,
        overnightRiskAtRiskPct,
        overnightRiskSafePct,
        overnightRiskGridTiedPct,
        overnightRiskNoForecastPct,
      };
    },
    enabled: Boolean(orgId) && !appLoading,
    staleTime: 90 * 1000,
    refetchInterval: (query) => (query.state.status === 'error' ? false : 2 * 60 * 1000),
    retry: false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}
